import React, { useEffect, useState } from 'react';
import { Button, Form, Spinner, Table } from 'reactstrap';
import AsyncSelect from 'react-select/async';
import Select from 'react-select';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { toast } from 'react-toastify';
import {
  fetchEmployeeDelegatedApprovers,
  employeeCodeList,
  updateDelegateApprover,
} from '../../../store/apis/employeeService';
import DatePicker from '../../Common/DatePicker/DatePicker';
import { fetchDelegateEmployerAutoComplete } from '../../../store/apis/leaveService';
import validationSchema from './constants';
import CustomDropdownIndicator from './CustomDropdownIndicator';
import './DelegateApprover.scss';

function DelegateApprover() {
  const [newRowIndex, setNewRowIndex] = useState(null);
  const [originalData, setOriginalData] = useState([]);

  const queryClient = useQueryClient();
  // Form Setup
  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
    getValues,
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      approvers: [],
    },
    resolver: yupResolver(validationSchema),
  });

  // Set Up Field for Approvers
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'approvers',
  });

  // Fetch Delegated Approvers
  const { data: delegatedApprovers, isLoading } = useQuery({
    queryKey: ['employeeDelegatedApprovers'],
    queryFn: () => fetchEmployeeDelegatedApprovers(),
  });

  // Fetch Request Type Options
  const { data: requestTypeOptions } = useQuery({
    queryKey: ['requestTypeOptions'],
    queryFn: () =>
      employeeCodeList({
        codeType: 'CODES',
        param1: 'REQUEST_TYPE',
      }),
  });

  // Update Delegate Approver
  const { mutate: updateDelegateApproverMutate } = useMutation({
    mutationFn: payload => updateDelegateApprover(payload),
    onSuccess: () => {
      queryClient.invalidateQueries(['employeeDelegatedApprovers']);
      toast.success('Delegate Approver updated successfully', {
        className: 'toast-success',
      });
    },
    onError: () => {
      toast.error('Failed to update Delegate Approver', {
        className: 'toast-error',
      });
    },
  });

  // Custom Styles for Select Components
  const getSelectStyles = () => ({
    control: (base, state) => ({
      ...base,
      borderRadius: '8px',
      borderColor: state.isFocused
        ? '#86b7fe' // Focused border color
        : '#929398', // Default border color
      boxShadow: state.isFocused
        ? '0 0 0 0.15rem rgba(13,110,253,.25)' // same as Bootstrap input focus style
        : 'none',
      '&:hover': {
        borderColor: state.isFocused ? '#86b7fe' : '#929398',
      },
    }),
    valueContainer: base => ({
      ...base,
      padding: '0.5rem 0.9rem',
      cursor: 'text',
    }),
    indicatorsContainer: base => ({
      ...base,
      padding: '0',
    }),
    dropdownIndicator: base => ({
      ...base,
      padding: '0 6px',
    }),
    input: base => ({
      ...base,
      margin: '0',
      padding: '0',
    }),
    menuPortal: base => ({
      ...base,
      zIndex: 9999,
    }),
    indicatorSeparator: () => ({
      display: 'none',
    }),
  });

  // Check if the last row is complete
  const isLastRowComplete = () => {
    const approvers = getValues('approvers');
    if (!approvers.length) return true;

    const last = approvers[approvers.length - 1];

    return (
      last?.delegateApprover?.value &&
      last?.approvalType &&
      last?.fromDate &&
      last?.toDate &&
      last?.isActive
    );
  };

  useEffect(() => {
    if (!delegatedApprovers) return;

    const formatDate = d => {
      if (!d) return '';
      const [dd, mm, yyyy] = d.split('/');
      return `${yyyy}-${mm}-${dd}`;
    };

    const mapped = delegatedApprovers.map(item => ({
      approvalType: item?.ApprovalTyp?.trim(),
      isActive: item?.Active === 'Y' ? 'Y' : 'N',
      fromDate: formatDate(item?.DelegateFrom),
      toDate: formatDate(item?.DelegateTo),
      delegateApprover: {
        value: item?.DelegateApprover,
        label: item?.DelegateApproverName,
      },
    }));
    setOriginalData(mapped);
    reset({ approvers: mapped }); // only replace with existing data
  }, [delegatedApprovers, reset]);

  // Handle Loading State
  if (isLoading) {
    return (
      <div className="text-center">
        <Spinner color="primary" />
      </div>
    );
  }

  const handleAddNewRow = () => {
    if (!isLastRowComplete()) {
      toast.error('Please complete the current row before adding a new one.', {
        className: 'toast-error',
      });
      return;
    }

    append({
      approvalType: '',
      isActive: '',
      fromDate: '',
      toDate: '',
      delegateApprover: null,
    });

    setNewRowIndex(fields.length);
  };

  const onSubmit = data => {
    const toDateOnlyString = dateObj => {
      if (!dateObj) return '';
      const date = new Date(dateObj);
      const yyyy = date.getFullYear();
      const mm = String(date.getMonth() + 1).padStart(2, '0');
      const dd = String(date.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    };

    const cleaned = data.approvers.map(row => ({
      approvalType: row.approvalType,
      isActive: row.isActive,
      fromDate: toDateOnlyString(row.fromDate),
      toDate: toDateOnlyString(row.toDate),
      delApprover: row.delegateApprover.value,
    }));

    updateDelegateApproverMutate({ delegateApprovalDetails: cleaned });
  };

  // Render Table Body
  const renderTableBody = () =>
    fields.map((_, index) => (
      <tr key={index}>
        <td>{index + 1}</td>

        {/* Approval Type */}
        <td className="px-2 py-1">
          <Controller
            name={`approvers[${index}].approvalType`}
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                components={{ DropdownIndicator: CustomDropdownIndicator }}
                menuPortalTarget={document.body}
                classNamePrefix="react-select"
                options={requestTypeOptions?.map(option => ({
                  value: option.Code,
                  label: option.Description,
                }))}
                styles={getSelectStyles()}
                onChange={selected => field.onChange(selected?.value)}
                value={
                  requestTypeOptions
                    ?.map(option => ({
                      value: option.Code,
                      label: option.Description,
                    }))
                    .find(opt => opt.value === field.value) || null
                }
                noOptionsMessage={() => null}
              />
            )}
          />
          {errors.approvers?.[index]?.approvalType && (
            <div className="text-danger" style={{ padding: '0 0.9rem' }}>
              {errors.approvers[index].approvalType.message}
            </div>
          )}
        </td>

        {/* Delegate Approver */}
        <td className="px-2 py-1">
          <Controller
            name={`approvers[${index}].delegateApprover`}
            control={control}
            render={({ field }) => (
              <AsyncSelect
                placeholder="Search Delegate Approver"
                components={{ DropdownIndicator: CustomDropdownIndicator }}
                menuPortalTarget={document.body}
                styles={getSelectStyles()}
                {...field}
                cacheOptions
                defaultOptions
                loadOptions={async input => {
                  const results = await fetchDelegateEmployerAutoComplete({
                    prefixText: input,
                  });
                  return results.map(user => ({
                    value: user.ID,
                    label: user.Name,
                  }));
                }}
                onChange={field.onChange}
                noOptionsMessage={() => null}
              />
            )}
          />
          {errors.approvers?.[index]?.delegateApprover && (
            <div className="text-danger" style={{ padding: '0 0.9rem' }}>
              {errors.approvers[index].delegateApprover.message}
            </div>
          )}
        </td>

        {/* From Date */}
        <td className="px-2 py-1">
          <Controller
            name={`approvers[${index}].fromDate`}
            control={control}
            render={({ field }) => (
              <DatePicker
                {...field}
                className={`external-filter-input ${errors.approvers?.[index]?.fromDate ? 'is-invalid' : ''}`}
                error={!!errors.approvers?.[index]?.fromDate}
                // borderStyle="none"
              />
            )}
          />
          {errors.approvers?.[index]?.fromDate && (
            <div className="text-danger" style={{ padding: '0 0.9rem' }}>
              {errors.approvers[index].fromDate.message}
            </div>
          )}
        </td>

        {/* To Date */}
        <td className="px-2 py-1">
          <Controller
            name={`approvers[${index}].toDate`}
            control={control}
            render={({ field }) => (
              <DatePicker
                {...field}
                className={`external-filter-input ${errors.approvers?.[index]?.toDate ? 'is-invalid' : ''}`}
                error={!!errors.approvers?.[index]?.toDate}
              />
            )}
          />
          {errors.approvers?.[index]?.toDate && (
            <div className="text-danger" style={{ padding: '0 0.9rem' }}>
              {errors.approvers[index].toDate.message}
            </div>
          )}
        </td>

        {/* Active */}
        <td className="px-2 py-1">
          <Controller
            name={`approvers[${index}].isActive`}
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                components={{ DropdownIndicator: CustomDropdownIndicator }}
                menuPortalTarget={document.body}
                classNamePrefix="react-select"
                options={[
                  { value: 'Y', label: 'Yes' },
                  { value: 'N', label: 'No' },
                ]}
                styles={getSelectStyles()}
                onChange={selected => field.onChange(selected?.value)}
                value={
                  [
                    { value: 'Y', label: 'Yes' },
                    { value: 'N', label: 'No' },
                  ].find(opt => opt.value === field.value) || null
                }
                noOptionsMessage={() => null}
              />
            )}
          />
          {errors.approvers?.[index]?.isActive && (
            <div className="text-danger" style={{ padding: '0 0.9rem' }}>
              {errors.approvers[index].isActive.message}
            </div>
          )}
        </td>
      </tr>
    ));

  return (
    <Form onSubmit={handleSubmit(onSubmit)} className="delegate-approver-form">
      <div>
        <div className="table-responsive da-table-container">
          <Table
            className="custom-table align-middle text-nowrap"
            id="da-table"
          >
            <thead>
              <tr>
                <th className="fw-semibold">#</th>
                <th className="fw-semibold">Approval Type</th>
                <th className="fw-semibold ps-0">Delegate Approver</th>
                <th className="fw-semibold">Delegate From</th>
                <th className="fw-semibold">Delegate To</th>
                <th className="fw-semibold">Active</th>
              </tr>
            </thead>
            <tbody>{renderTableBody()}</tbody>
          </Table>
        </div>
        {errors.approvers && (
          <div className="text-danger px-3 pb-3">
            {errors.approvers.message}
          </div>
        )}
        <div className="d-flex flex-row align-items-center justify-content-between px-3 pb-3">
          <Button onClick={handleAddNewRow} className="custom-light-btn px-4">
            Add
          </Button>
          <div className="d-flex flex-row align-items-center gap-2">
            <Button
              className="custom-danger-btn px-4"
              onClick={() => {
                if (newRowIndex !== null) {
                  remove(newRowIndex);
                  setNewRowIndex(null);
                }
                reset(
                  { approvers: originalData }, // field values
                  { keepDirty: false, keepTouched: false }, // reset form state
                );
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="custom-btn px-4"
              disabled={!isDirty}
            >
              Submit
            </Button>
          </div>
        </div>
      </div>
    </Form>
  );
}

export default DelegateApprover;
